import {
  Component,
  input,
  output,
  model,
  signal,
  computed,
  contentChildren,
  effect,
  AfterContentInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  M3ButtonGroupItemDirective,
  ButtonGroupItemPosition,
} from './m3-button-group-item.directive';

export type M3ButtonGroupMode = 'action' | 'single' | 'multi';
export type M3ButtonGroupVariant = 'standard' | 'contained';
export type M3ButtonGroupSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl';

@Component({
  selector: 'app-m3-button-group',
  standalone: true,
  template: `
    <div
      [class]="containerClass()"
      [attr.role]="containerRole()"
      [attr.aria-label]="ariaLabel()"
      [attr.aria-multiselectable]="mode() === 'multi' || null"
      (keydown)="handleKeyDown($event)"
    >
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }

      .m3-button-group {
        display: inline-flex;
        gap: 0;
        align-items: stretch;

        &.variant-contained {
          background: var(--md-sys-color-surface-container);
          border-radius: var(--md-sys-shape-corner-large, 16px);
          padding: 4px;
          border: 1px solid var(--md-sys-color-outline-variant);
        }

        &.variant-standard {
          gap: 1px;
        }
      }

      :host ::ng-deep {
        .button-group-item-first {
          app-m3-button button,
          app-m3-icon-button button,
          app-m3-toggle-button button,
          app-m3-toggle-icon-button button {
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
          }
        }

        .button-group-item-middle {
          app-m3-button button,
          app-m3-icon-button button,
          app-m3-toggle-button button,
          app-m3-toggle-icon-button button {
            border-radius: 0 !important;
          }
        }

        .button-group-item-last {
          app-m3-button button,
          app-m3-icon-button button,
          app-m3-toggle-button button,
          app-m3-toggle-icon-button button {
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;
          }
        }

        .variant-contained {
          .button-group-item-first:not(:last-child)::after,
          .button-group-item-middle::after {
            content: '';
            position: absolute;
            right: -1px;
            top: 25%;
            height: 50%;
            width: 1px;
            background: var(--md-sys-color-outline-variant);
            pointer-events: none;
          }

          [appM3ButtonGroupItem] {
            position: relative;
          }
        }

        .variant-standard {
          .button-group-item-first:not(:last-child)::after,
          .button-group-item-middle::after {
            content: '';
            position: absolute;
            right: -1px;
            top: 20%;
            height: 60%;
            width: 1px;
            background: var(--md-sys-color-outline-variant);
            pointer-events: none;
            z-index: 1;
          }

          [appM3ButtonGroupItem] {
            position: relative;
          }
        }

        .button-group-item-selected {
          app-m3-button button,
          app-m3-icon-button button {
            background: var(--md-sys-color-secondary-container) !important;
            color: var(--md-sys-color-on-secondary-container) !important;
          }
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .m3-button-group {
          transition: none;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class M3ButtonGroupComponent<T = unknown> implements AfterContentInit {
  mode = input<M3ButtonGroupMode>('action');
  variant = input<M3ButtonGroupVariant>('contained');
  size = input<M3ButtonGroupSize>('default');
  ariaLabel = input<string>();

  value = model<T | undefined>(undefined);
  values = model<T[]>([]);

  selectionChange = output<T | T[]>();

  items = contentChildren(M3ButtonGroupItemDirective);

  private focusedIndex = signal<number>(0);

  containerClass = computed(() => {
    return [
      'm3-button-group',
      `variant-${this.variant()}`,
      `size-${this.size()}`,
    ].join(' ');
  });

  containerRole = computed(() => {
    switch (this.mode()) {
      case 'single':
        return 'radiogroup';
      case 'multi':
        return 'listbox';
      default:
        return 'group';
    }
  });

  constructor() {
    effect(() => {
      const itemList = this.items();
      const index = this.focusedIndex();

      itemList.forEach((item, i) => {
        item.setTabIndex(i === index ? 0 : -1);
      });
    });

    effect(() => {
      const itemList = this.items();

      itemList.forEach((item, i) => {
        let pos: ButtonGroupItemPosition;
        if (itemList.length === 1) {
          pos = 'only';
        } else if (i === 0) {
          pos = 'first';
        } else if (i === itemList.length - 1) {
          pos = 'last';
        } else {
          pos = 'middle';
        }
        item.setPosition(pos);
      });
    });

    effect(() => {
      const itemList = this.items();
      const currentMode = this.mode();
      const currentValue = this.value();
      const currentValues = this.values();

      if (currentMode === 'action') {
        itemList.forEach((item) => item.setSelected(false));
        return;
      }

      if (currentMode === 'single') {
        itemList.forEach((item) => {
          item.setSelected(item.value() === currentValue);
        });
        return;
      }

      if (currentMode === 'multi') {
        itemList.forEach((item) => {
          const itemValue = item.value() as T;
          item.setSelected(currentValues.includes(itemValue));
        });
      }
    });
  }

  ngAfterContentInit(): void {
    const itemList = this.items();
    if (itemList.length > 0) {
      this.focusedIndex.set(0);
    }

    itemList.forEach((item, index) => {
      const element = (
        item as unknown as { elementRef: { nativeElement: HTMLElement } }
      ).elementRef?.nativeElement;
      if (element) {
        element.addEventListener('click', () => this.handleItemClick(index));
      }
    });
  }

  private handleItemClick(index: number): void {
    const itemList = this.items();
    const item = itemList[index];
    if (!item || item.disabled()) return;

    this.focusedIndex.set(index);

    if (this.mode() === 'action') {
      return;
    }

    const itemValue = item.value() as T;

    if (this.mode() === 'single') {
      this.value.set(itemValue);
      this.selectionChange.emit(itemValue);
      return;
    }

    if (this.mode() === 'multi') {
      const currentValues = [...this.values()];
      const valueIndex = currentValues.indexOf(itemValue);

      if (valueIndex >= 0) {
        currentValues.splice(valueIndex, 1);
      } else {
        currentValues.push(itemValue);
      }

      this.values.set(currentValues);
      this.selectionChange.emit(currentValues);
    }
  }

  handleKeyDown(event: KeyboardEvent): void {
    const itemList = this.items().filter((item) => !item.disabled());
    const currentIndex = this.focusedIndex();

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.moveFocus(-1, itemList, currentIndex);
        break;

      case 'ArrowRight':
        event.preventDefault();
        this.moveFocus(1, itemList, currentIndex);
        break;

      case 'Home':
        event.preventDefault();
        this.focusFirst(itemList);
        break;

      case 'End':
        event.preventDefault();
        this.focusLast(itemList);
        break;

      case ' ':
      case 'Enter':
        if (this.mode() !== 'action') {
          event.preventDefault();
          const focusedItem = itemList[currentIndex];
          if (focusedItem) {
            focusedItem.click();
          }
        }
        break;
    }
  }

  private moveFocus(
    direction: number,
    itemList: readonly M3ButtonGroupItemDirective[],
    currentIndex: number
  ): void {
    if (itemList.length === 0) return;

    let newIndex = currentIndex + direction;

    if (newIndex < 0) {
      newIndex = itemList.length - 1;
    } else if (newIndex >= itemList.length) {
      newIndex = 0;
    }

    this.focusedIndex.set(newIndex);
    this.focusItemAtIndex(newIndex);
  }

  private focusFirst(itemList: readonly M3ButtonGroupItemDirective[]): void {
    if (itemList.length > 0) {
      this.focusedIndex.set(0);
      this.focusItemAtIndex(0);
    }
  }

  private focusLast(itemList: readonly M3ButtonGroupItemDirective[]): void {
    if (itemList.length > 0) {
      const lastIndex = itemList.length - 1;
      this.focusedIndex.set(lastIndex);
      this.focusItemAtIndex(lastIndex);
    }
  }

  private focusItemAtIndex(index: number): void {
    const itemList = this.items().filter((item) => !item.disabled());
    const item = itemList[index];
    if (item) {
      item.focus();
    }
  }
}
